import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { apiRequest } from "@/lib/queryClient";
import { trackConversion } from "@/lib/analytics";

export function WaitlistSection() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [ref, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.15 });
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const consentLabel = t(
    "waitlist.marketingConsent",
    "I agree to receive Troopn launch updates by email. I can unsubscribe anytime.",
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || status === "loading" || status === "success") return;
    if (!marketingConsent) {
      setStatus("error");
      setErrorMessage(t("waitlist.consentRequired", "Please agree to receive launch updates by email."));
      return;
    }

    setStatus("loading");
    setErrorMessage("");

    try {
      await apiRequest("POST", "/api/waitlist", {
        email: email.trim(),
        source: "landing",
        marketingConsent: true,
        consentText: consentLabel,
      });
      setStatus("success");
      trackConversion("waitlist", { source: "landing" });
      navigate("/thank-you?type=waitlist");
    } catch (err: any) {
      setStatus("error");
      const msg = err?.message || "";
      if (msg.includes("400")) {
        setErrorMessage(t("waitlist.invalidEmail"));
      } else {
        setErrorMessage(t("waitlist.error"));
      }
    }
  };

  return (
    <section
      ref={ref}
      className="relative py-20 md:py-28 overflow-hidden bg-brand-navy"
      data-testid="section-waitlist"
    >
      {/* Subtle gradient accents */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, hsl(var(--brand-coral) / 0.06) 0%, transparent 60%)",
        }}
      />

      <div
        className={`relative z-10 max-w-2xl mx-auto px-6 text-center transition-all duration-1000 ease-out ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="rounded-full bg-brand-coral/10 p-4">
            <Mail className="h-7 w-7 text-brand-coral" />
          </div>
        </div>

        <Heading
          level={2}
          serif
          className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4"
        >
          {t("waitlist.title")}
        </Heading>

        <Text className="text-base sm:text-lg text-primary-foreground/80 mb-10 max-w-lg mx-auto">
          {t("waitlist.subtitle")}
        </Text>

        {status === "success" ? (
          <div className="flex items-center justify-center gap-3 text-brand-coral animate-reveal-up">
            <CheckCircle2 className="h-6 w-6" />
            <span className="text-lg font-semibold">{t("waitlist.success")}</span>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-3 max-w-md mx-auto"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="email"
                placeholder={t("waitlist.placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={status === "loading"}
                className="flex-1 h-12 bg-white/[0.08] border-white/10 text-white placeholder:text-white/80 focus-visible:ring-brand-coral/50 rounded-full px-5"
                data-testid="input-waitlist-email"
              />
              <Button
                type="submit"
                disabled={status === "loading" || !email.trim() || !marketingConsent}
                className="h-12 bg-brand-coral hover:bg-brand-coral/90 text-white font-bold rounded-full px-8 transition-all duration-300 hover:scale-[1.03] shadow-lg shadow-brand-coral/20"
                data-testid="button-waitlist-join"
              >
                {status === "loading" ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t("waitlist.join")
                )}
              </Button>
            </div>
            <label className="flex items-start gap-2 text-left text-xs text-primary-foreground/80 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                data-testid="checkbox-waitlist-marketing"
              />
              <span>{consentLabel}</span>
            </label>
          </form>
        )}

        {status === "error" && errorMessage && (
          <p className="text-red-400 text-sm mt-3">{errorMessage}</p>
        )}

        <p className="text-xs text-primary-foreground/80 mt-6">{t("waitlist.privacy")}</p>
      </div>
    </section>
  );
}
